<template>
  <a
    v-if="ctaLink"
    :id="id"
    :href="ctaLink"
    target="_blank"
    rel="noopener"
    :class="$style.serviceBox"
    @click="handleClick()"
  >
    <div>
      <HgIcon :themeClass="$style.icon" :icon="icon" size="2x-large" />
      <h5 :class="$style.title">{{ t(title) }}</h5>
      <p :class="$style.description">{{ t(description) }}</p>
    </div>
    <p :class="{ [$style.cta]: true, [$style.cta_is24]: isPlatformIs24() }">
      {{ t(link.text) }}
    </p>
  </a>
  <div v-else :id="id" :class="$style.serviceBox" @click="handleClick()">
    <div>
      <HgIcon :themeClass="$style.icon" :icon="icon" size="2x-large" />
      <h5 :class="$style.title">{{ t(title) }}</h5>
      <p :class="$style.description">{{ t(description) }}</p>
    </div>
    <p :class="{ [$style.cta]: true, [$style.cta_is24]: isPlatformIs24() }">
      {{ t(link.text) }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import { HgIcon, Icon } from '@smg-real-estate/domus';
import { computed } from 'vue';

import { resolveUrl } from '@/helpers/HgLink/HgLinkHelper';
import { isPlatformIs24 } from '@/helpers/platformDetection';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

defineOptions({
  name: 'ServiceBox',
});

interface Props {
  id: string;
  icon: Icon;
  title: string;
  description: string;
  link: {
    href: string | undefined;
    text: string;
    hrefSkipTranslation?: boolean;
  };
}
const props = defineProps<Props>();

const emit = defineEmits<{
  linkClicked: [];
}>();

const ctaLink = computed((): string | undefined => {
  if (!props.link.href) {
    return props.link.href;
  }
  // Use hrefSkipTranslation property to determine if translation should be skipped
  if (props.link.hrefSkipTranslation) {
    // Use the URL directly without translation
    return resolveUrl(props.link.href);
  } else {
    // Treat as translation key
    return resolveUrl(t(props.link.href).toString());
  }
});

const handleClick = () => {
  emit('linkClicked');
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

.serviceBox {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  height: 20.8125rem;
  padding: $space-200 $space-100;
  text-align: center;
  cursor: pointer;
  background: $color-white;
  border-radius: $border-radius-l;
  box-shadow: $elevation-minimal;

  &:hover,
  &:active {
    text-decoration: none;
    box-shadow: $elevation-medium;
  }
}

.icon {
  margin-bottom: $space-100;
  fill: var(--theme-color-secondary);
}

.title {
  margin: 0 0 $space-50 0;

  @include body-2-bold($color-black);
}

.description {
  @include body-2($color-black);
}

.cta {
  @include font-bold;

  margin: 0;
  color: var(--theme-color-secondary);
}

.cta_is24 {
  color: var(--base-color-link);
}
</style>
